import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Dropdown multi-select for workers. Shows a trigger + selected chips below,
// instead of listing every worker inline.
export function WorkerSelect({ workers = [], value = [], onChange, testid = "worker-select", placeholder = "Select workers…" }) {
  const [open, setOpen] = useState(false);
  const selected = workers.filter((w) => value.includes(w.id));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  return (
    <div data-testid={`${testid}-wrap`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid}
            className="mt-1.5 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1">
            <span className={cn("truncate", selected.length === 0 && "text-muted-foreground")}>
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[16rem]" align="start">
          <Command>
            <CommandInput placeholder="Search workers…" data-testid={`${testid}-input`} />
            <CommandList>
              {workers.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">No workers</div>
              ) : (
                <CommandGroup>
                  {[...workers].sort((a, b) => (a.displayName || "").localeCompare(b.displayName || "")).map((w) => (
                    <CommandItem key={w.id} value={w.displayName} onSelect={() => toggle(w.id)} data-testid={`${testid}-option-${w.id}`}>
                      <Check className={cn("h-4 w-4", value.includes(w.id) ? "opacity-100 text-brand" : "opacity-0")} />
                      <span className="truncate">{w.displayName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-chips`}>
          {selected.map((w) => (
            <span key={w.id} className="inline-flex items-center gap-1 rounded-full bg-brand/10 text-brand text-xs font-semibold px-2.5 py-1">
              {w.displayName}
              <button type="button" data-testid={`${testid}-remove-${w.id}`} onClick={() => toggle(w.id)} className="hover:text-brand-dark">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
